import { shift } from '@floating-ui/core';
import { DeprecatedTaskIcon, Flex, Popover, ToastAnchor, Tooltip, showToast } from '@lokalise/louis';
import { useEffect } from 'react';

import { useGetBadgesQuery } from '@app/api/tasks';
import useDispatch from '@app/hooks/useDispatch';
import useHashState from '@app/hooks/useHashState';
import useSelector from '@app/hooks/useSelector';
import { getSession } from '@app/selectors';
import type { TaskClosedPayload } from '@app/sockets/socketTypes';
import { defaultConnection } from '@app/sockets/sockets';

import { badgeActions } from '../../../../store/badges/reducers';
import { projectsActions } from '../../../ProjectsList/reducers';
import { getTasksBadge } from '../../../ProjectsList/selectors';
import TasksPopover from './TasksPopover';
import { Badge, NavItem } from './styles';

type SidebarWidgetProps = {
    projectId: string;
};

const url = new URL(window.location.href);
const aiPostEditingAnchor = url.href.includes('#aiPostEditing');

const SidebarWidget = ({ projectId }: SidebarWidgetProps) => {
    const dispatch = useDispatch();
    const { data: badgesResponse } = useGetBadgesQuery();

    const { userId } = useSelector(getSession);

    const [openTaskId, setOpenTaskId] = useHashState('task');

    if (aiPostEditingAnchor) {
        // Wait 5 seconds before showing ai post editing intercom automation
        setTimeout(() => {
            document.querySelector<HTMLButtonElement>('#aiPostEditingBotTrigger')?.click();
        }, 5000);
    }

    useEffect(() => {
        if (badgesResponse) {
            // Will need to sort this out later but right now just bringing it all together
            dispatch(projectsActions.setBadgesData(badgesResponse.projects));
            dispatch(projectsActions.setTaskBadge(badgesResponse.widget));
            dispatch(badgeActions.setTaskBadges({ tasksBadges: badgesResponse.projects }));
        }
    }, [dispatch, badgesResponse]);

    useEffect(() => {
        const handleTaskClosedToast = (data: TaskClosedPayload) => {
            const { task_id, taskType } = data;
            // data.createdBy is a number
            const wasCreatedByCurrentUser = String(data.createdBy) === userId;
            const isAutomaticTranslationTask = taskType === 'automatic_translation';

            if (isAutomaticTranslationTask && wasCreatedByCurrentUser) {
                const editorUrl = `/project/${data.projectId}/?branch=&filter=task_${task_id}&view=multi#aiPostEditing`;

                showToast({
                    type: 'success',
                    title: 'Task complete',
                    description: () => (
                        <Flex direction="column" gap={2}>
                            Translations are now in editor.
                            <ToastAnchor href={editorUrl}>Go to editor</ToastAnchor>
                        </Flex>
                    ),
                });
            }
        };

        defaultConnection.on('task closed', handleTaskClosedToast);

        return () => {
            defaultConnection.off('task closed', handleTaskClosedToast);
        };
    }, [userId]);

    const badge = useSelector(getTasksBadge);

    return (
        <Tooltip tooltip="Tasks" placement="right">
            <Popover
                content={<TasksPopover projectId={projectId} focusTaskId={openTaskId} />}
                placement="right"
                open={openTaskId ? true : undefined}
                onOpenChange={(visible) => {
                    if (!visible && openTaskId) {
                        setOpenTaskId(undefined);
                    }
                }}
                middlewares={[
                    shift({
                        // Adding some spacing to the top and bottom so the popover doesn't extend to the edges
                        // of the screen.
                        padding: {
                            top: 32,
                            bottom: 32,
                        },
                    }),
                ]}
            >
                {(visible) => (
                    <NavItem aria-label="Tasks" $active={visible}>
                        <DeprecatedTaskIcon size="24px" />
                        {badge > 0 && (
                            <Badge role="status" aria-label={`${badge} items requiring attention`}>
                                {badge > 9 ? '9+' : badge}
                            </Badge>
                        )}
                    </NavItem>
                )}
            </Popover>
        </Tooltip>
    );
};

export default SidebarWidget;
